import type { TableModel } from './index';
import { effect } from '@preact/signals-react';
import { deferred } from '@thinkalpha/ui-core/deferred';

export type TableConstructionWait = 'constructed' | 'timed-out';

export interface WaitForTableConstructionOptions {
    /** How long to wait before giving up; the caller then takes whatever the table holds. */
    readonly timeoutMs: number;
    readonly signal?: AbortSignal;
}

/**
 * Waits until a table is connected and has finished construction (`constructionComplete`), or
 * until the timeout passes. A table stuck pending, or one that never reports progress, times out
 * rather than failing, so a caller can still read the rows it has. Rejects with the signal's reason
 * when aborted.
 */
export async function waitForTableConstruction(
    table: Pick<TableModel, 'connectionStatus' | 'constructionComplete'>,
    { timeoutMs, signal }: WaitForTableConstructionOptions,
): Promise<TableConstructionWait> {
    signal?.throwIfAborted();
    using stack = new DisposableStack();
    const done = deferred<TableConstructionWait>();

    const onAbort = () => done.reject(signal?.reason);
    signal?.addEventListener('abort', onAbort, { once: true });
    stack.defer(() => signal?.removeEventListener('abort', onAbort));

    stack.adopt(
        setTimeout(() => done.resolve('timed-out'), timeoutMs),
        clearTimeout,
    );
    stack.defer(
        effect(() => {
            if (table.connectionStatus.value === 'connected' && table.constructionComplete.value) {
                done.resolve('constructed');
            }
        }),
    );

    return await done.promise;
}
